import React, { useContext, useEffect, useState } from "react";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { injectIntl, useIntl } from "react-intl";
import useLayerEscapeClose from "src/hooks/useLayerEscapeClose";
import { AccessLinkFieldContext } from "../AccessLinkField";
import Modal, {
  ModalText,
  ModalTitle,
  OutsideClickModes,
} from "src/app/components/Modal/Modal";
import { useSelector } from "react-redux";
import { accessLinkVirtualFields, FormTabs } from "./utils";
import { TaskType } from "src/types";
import Tabs from "src/app/components/Tabs/Tabs";
import Tab from "src/app/components/Tabs/Tab";

import "./CreateFormModal.scss";
import { CreateFormGeneral } from "./tabs/CreateFormGeneral";
import { CreateFormFields } from "./tabs/CreateFormFields";
import { translateMessage } from "src/app/methods/translateMessage";

export const formTypeOptions = [
  {
    label: (
      <IDHFormattedMessage
        id="ws_communication_form"
        defaultMessage="Communication Form"
      />
    ),
    value: "general",
  },
  {
    label: (
      <IDHFormattedMessage
        id="ws_content_proposal"
        defaultMessage="Content Proposal"
      />
    ),
    value: "contentProposal",
  },
];

function CreateFormModal() {
  const {
    accessLinkData,
    setAccessLinkData,
    showDetailsPopup,
    setShowDetailsPopup,
    selectedVals,
    setSelectedVals,
    noManageModal,
    handleConfirm,
    onManageClose,
  } = useContext(AccessLinkFieldContext);

  const [activeTab, setActiveTab] = useState(FormTabs.General);

  const { taskType } = useSelector((state) => state.projectReducer);

  const getType = () => {
    const found = formTypeOptions.find((t) => t.value === accessLinkData.type);
    if (found) return found;

    return taskType === TaskType.Content
      ? formTypeOptions[1]
      : formTypeOptions[0];
  };

  const [caption, setCaption] = useState(accessLinkData.caption);
  const [description, setDescription] = useState(accessLinkData.description);
  const [successMessage, setSuccessMessage] = useState(
    accessLinkData.successMessage,
  );
  const [type, setType] = useState(() => getType());
  const [loading, setLoading] = useState(false);

  const intl = useIntl();

  const isContent = taskType === TaskType.Content;

  useEffect(() => {
    const contentDefaultMessage = translateMessage({
      intl,
      id: "ws_your_content_has_been_successfully_submitted_for_review",
      defaultMessage:
        "Your content has been successfully submitted for review.",
    });
    const baseDefaultMessage = translateMessage({
      intl,
      id: "ws_your_response_has_been_submitted_successfully_thank_you_for_providing_the_requested_information",
      defaultMessage:
        "Your response has been submitted successfully. Thank you for providing the requested information.",
    });
    const defaultMessages = [contentDefaultMessage, baseDefaultMessage];
    const defaultMessage =
      defaultMessages[type.value === "contentProposal" ? 0 : 1];

    if (
      accessLinkData?.editMode ||
      (successMessage?.length > 0 && !defaultMessages.includes(successMessage))
    )
      return;

    setSuccessMessage(defaultMessage);
  }, [accessLinkData, taskType, type]);

  useEffect(() => {
    if (type.value === "contentProposal") {
      const hasAnyVirtualField = selectedVals.find(
        (v) => v.field?.value === "virtualMetaField",
      );
      const filteredFields = selectedVals.filter((v) => v.field?.disabled);

      if (!hasAnyVirtualField) {
        setSelectedVals([...filteredFields, ...accessLinkVirtualFields]);
      }
    } else {
      setSelectedVals((vals) =>
        vals.filter((item) => item.field?.value !== "virtualMetaField"),
      );
    }
  }, [type]);

  const handleClose = () => {
    setShowDetailsPopup(false);
    if (noManageModal) {
      onManageClose();
    }
  };

  useLayerEscapeClose(handleClose);

  const sendData = () => {
    setAccessLinkData({
      metaFields: selectedVals.map((t) => {
        if (t.field?.value === "virtualMetaField") {
          return {
            virtualMetaFieldName: t.field.label,
            virtualMetaFieldType: t.field.metafieldType,
            wsMetaFieldAccessMode: t.mode,
            wsMetaFieldDescription: t.wsMetaFieldDescription,
            wsMetaFieldIsRequired: t.required,
            wsMetaFieldType: "virtualMetaField",
            wsMetaFieldUuid: null,
          };
        }

        return {
          wsMetaFieldType: t.field.metafieldType,
          wsMetaFieldUuid: t.field.value,
          wsMetaFieldAccessMode: t.mode,
          wsMetaFieldIsRequired: t.required,
          wsMetaFieldDescription: t.wsMetaFieldDescription,
        };
      }),
      caption,
      description,
      successMessage,
      type: type.value,
    });
  };

  useEffect(() => {
    noManageModal && sendData();
  }, [caption, description, selectedVals]);

  if (!showDetailsPopup) return null;

  const renderTabContent = () => {
    switch (activeTab) {
      case FormTabs.General:
        return (
          <CreateFormGeneral
            caption={caption}
            setCaption={setCaption}
            description={description}
            setDescription={setDescription}
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
            type={type}
            setType={setType}
            isContent={isContent}
          />
        );
      case FormTabs.Fields:
        return <CreateFormFields type={type} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      onClose={handleClose}
      className="details-popup"
      overlayClassName="details-popup__overlay"
      width={600}
      confirmButtonText={
        noManageModal ? (
          <IDHFormattedMessage id="ws_create" defaultMessage="Create" />
        ) : (
          <IDHFormattedMessage id="ws_set" defaultMessage="Set" />
        )
      }
      onConfirmClick={() => {
        setLoading(true);
        sendData();
        if (noManageModal) {
          handleConfirm(null, () => setShowDetailsPopup(false));
        } else setShowDetailsPopup(false);
      }}
      confirmButtonDisabled={selectedVals.length === 0}
      displayCancelButton
      loading={loading}
      disableAutoFocusForSelectors
      outsideClick={OutsideClickModes.Warn}
    >
      <ModalTitle>
        <IDHFormattedMessage
          id="ws_creating_new_form"
          defaultMessage="Creating new form"
        />
      </ModalTitle>

      <ModalText>
        <IDHFormattedMessage
          id="ws_details_popup_paragraph"
          defaultMessage="Create a form that can be shared with people outside the platform. Include a clear title and description that will help them understand the purpose of the form."
        />
      </ModalText>

      <Tabs>
        <Tab
          tabText={
            <IDHFormattedMessage id="ws_general" defaultMessage="General" />
          }
          active={activeTab === FormTabs.General}
          onClick={() => setActiveTab(FormTabs.General)}
        />
        <Tab
          tabText={
            <IDHFormattedMessage id="ws_fields" defaultMessage="Fields" />
          }
          active={activeTab === FormTabs.Fields}
          onClick={() => setActiveTab(FormTabs.Fields)}
        />
      </Tabs>

      {renderTabContent()}
    </Modal>
  );
}

export default injectIntl(CreateFormModal);
